import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import Overlay from '../../shared/components/Overlay';
import { useGetReportDealsQuery } from './apiSlice';
import { accountSelector } from '../../shared/store/shared/accountSlice';
import { AllDeal, AgreementApiModel } from '../../shared/models/DealModel';
import userAdminNavigate from '../../shared/hooks/userAdminNavigate';
import DealTab from '../../shared/components/DealTab';
import hasCustomDateRange from './hasCustomDateRange';
import ReportsOverview from './ReportsOverview';
import dateParam from './dateParam';
import usePermissions from '../../shared/hooks/usePermissions';
import PERMISSIONS from '../../shared/permissions';
import useAccountAlerts from '../../shared/components/AlertGroup/useAccountAlerts';
import useAdminVisitedPages from '../../shared/hooks/useAdminVisitedPages';
import useGetDeals from '../../shared/hooks/useGetDeals';
import AdminPayByUserNotification from '../../shared/components/AdminPayByUserNotification';
import { adminLayoutSelector } from '../adminLayoutSlice';

let prevDealId = '';
export default function Reports() {
  const { hasAccess, isLoading: isPermLoading } = usePermissions();
  const { payByUserAgreements } = useSelector(adminLayoutSelector);

  const { isAccountAlertFetching, isAccountAlertLoading } = useAccountAlerts({
    dynamicAlerts: true,
  });
  const {
    data: dealList,
    isLoading: isDealListLoading,
    isFetching: isDealListFetching,
    selectedHomeDeal: selectedDeal,
    setSelectedHomeDeal: setSelectedDeal,
  } = useGetDeals({
    allDealsEnabled: hasAccess({
      permissions: [PERMISSIONS.ReportingAllAgreementsAccess],
    }),
    overviewOnly: true,
  });
  const { currentAccountId } = useSelector(accountSelector);
  const { navigate } = userAdminNavigate();
  const [param, setParam] = useState({
    accountId: currentAccountId,
    dealId: '',
    StartDate: null,
    EndDate: null,
  });
  const {
    data: reportDeals,
    isLoading,
    isFetching,
  } = useGetReportDealsQuery(param, {
    skip:
      !dealList ||
      !param.dealId ||
      (param.dealId === AllDeal.id &&
        !hasAccess({
          permissions: [PERMISSIONS.ReportingAllAgreementsAccess],
        })),
  });
  const { dateRange, dealId } = useParams();
  const [reportType, setReportType] = useState(dateRange || 'All Time');
  const [startDate, setStartDate] = useState(
    hasCustomDateRange(reportType)
      ? new Date(reportType.split('-')[0].split(':')[1])
      : undefined,
  );
  const [endDate, setEndDate] = useState(
    hasCustomDateRange(reportType)
      ? new Date(reportType.split('-')[1])
      : undefined,
  );
  const startDateParam = dateParam({
    reportType,
    type: 'start',
    startDate,
    endDate,
  });
  const endDateParam = dateParam({
    reportType,
    type: 'end',
    startDate,
    endDate,
  });

  // Attach the event listener when the component mounts
  useEffect(() => {
    // Store the original window.scrollTo method
    const originalScrollTo = window.scrollTo;

    if (prevDealId !== dealId) {
      prevDealId = dealId;
      // Override window.scrollTo with a custom function that does nothing
      window.scrollTo = () => {};
    }

    // Clean up and restore the original window.scrollTo method when the component unmounts
    return () => {
      window.scrollTo = originalScrollTo;
    };
  }, [dealId]);

  useEffect(() => {
    if (dealList && !isDealListLoading && !isDealListFetching && selectedDeal) {
      setParam({
        accountId: currentAccountId,
        dealId: selectedDeal.id,
        StartDate: startDateParam,
        EndDate: endDateParam,
      });
      // handle auto navigate on selectedDeal change as well on page load
      navigate(`reports/${selectedDeal.id}/${reportType}`);
    }
  }, [
    isDealListLoading,
    isDealListFetching,
    dealList,
    selectedDeal,
    reportType,
  ]);

  const handleDealChange = (deal: AgreementApiModel) => {
    setSelectedDeal(deal);
  };
  const visits = useAdminVisitedPages('reports');
  useEffect(() => {
    if (
      !isLoading &&
      !isDealListLoading &&
      !isPermLoading &&
      !isAccountAlertLoading &&
      !visits.isLoading
    ) {
      visits.performFirstTimeVisit('table tbody tr');
    }
  }, [
    isLoading,
    isDealListLoading,
    isPermLoading,
    isAccountAlertLoading,
    visits.isLoading,
  ]);
  return (
    <div>
      {selectedDeal && dealList.length > 1 && (
        <div className="mb-4">
          <DealTab
            onTabChange={handleDealChange}
            selectedDeal={selectedDeal}
            deals={dealList}
            showAllSelector={hasAccess({
              permissions: [PERMISSIONS.ReportingAllAgreementsAccess],
            })}
            pinPayByUser
          />
        </div>
      )}
      {reportDeals && (
        <>
          <AdminPayByUserNotification agreementId={selectedDeal.id} />
          <ReportsOverview
            isPayByUser={payByUserAgreements.includes(selectedDeal.id)}
            reportDeals={reportDeals}
            reportType={reportType}
            setReportType={setReportType}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            startDateParam={startDateParam}
            endDateParam={endDateParam}
          />
        </>
      )}

      <Overlay
        variant="PMIOverlay"
        global
        animated
        fade
        iconColor="brand-dark"
        overlayColor="black"
        overlayOpacity={1}
        show={
          isLoading ||
          isFetching ||
          isDealListLoading ||
          isDealListFetching ||
          isPermLoading ||
          isAccountAlertFetching ||
          isAccountAlertLoading ||
          visits.isLoading
        }
      />
    </div>
  );
}
